import React, { useEffect, useState, useMemo } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const CATEGORIES = ['ROOM','COMMON_AREA','EQUIPMENT','ELECTRICAL','PLUMBING','HVAC','FURNITURE','OTHER'];
const PRIORITIES = ['CRITICAL','HIGH','MEDIUM','LOW'];
const STATUSES = ['OPEN','ASSIGNED','IN_PROGRESS','ON_HOLD','COMPLETED','VERIFIED','CLOSED','CANCELLED'];
const SOURCES = ['FRONT_OFFICE','HOUSEKEEPING','GUEST_REQUEST','ENGINEERING'];

const PRIORITY_COLORS = { CRITICAL: '#dc3545', HIGH: '#fd7e14', MEDIUM: '#ffc107', LOW: '#28a745' };
const STATUS_COLORS = {
  OPEN: '#0d6efd', ASSIGNED: '#6f42c1', IN_PROGRESS: '#fd7e14', ON_HOLD: '#6c757d',
  COMPLETED: '#20c997', VERIFIED: '#28a745', CLOSED: '#155724', CANCELLED: '#dc3545'
};

const badge = (text, bg) => ({
  display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
  color: '#fff', backgroundColor: bg, whiteSpace: 'nowrap'
});

function MaintWorkOrders({ token, selectedProperty, role, permissions }) {
  const { isMobile } = useIsMobile();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // filters
  const [fStatus, setFStatus] = useState('');
  const [fPriority, setFPriority] = useState('');
  const [fCategory, setFCategory] = useState('');

  // create modal
  const [showCreate, setShowCreate] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [form, setForm] = useState(emptyForm());

  // assign modal
  const [assignWO, setAssignWO] = useState(null);
  const [assignTechId, setAssignTechId] = useState('');

  // prompt modal (for cancel reason / resolution notes)
  const [promptWO, setPromptWO] = useState(null);
  const [promptType, setPromptType] = useState(''); // 'CANCELLED' | 'COMPLETED'
  const [promptText, setPromptText] = useState('');

  const hasMaint = role === 'admin' || permissions?.hotel_admin || permissions?.maintenance;

  const headers = useMemo(() => ({
    Authorization: 'Bearer ' + token,
    'x-chain-id': selectedProperty?.chain_id,
    'x-property-id': selectedProperty?.id
  }), [token, selectedProperty]);

  function emptyForm() {
    return {
      category: 'ROOM', sub_category: '', room_id: '', asset: '', location: '',
      priority: 'MEDIUM', description: '', mark_room_ooo: false, reported_source: 'FRONT_OFFICE'
    };
  }

  /* ---------- data loaders ---------- */

  const loadRows = async () => {
    if (!selectedProperty?.id) return setRows([]);
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fStatus) params.set('status', fStatus);
      if (fPriority) params.set('priority', fPriority);
      if (fCategory) params.set('category', fCategory);
      const qs = params.toString();
      const res = await fetch(`/api/v1/maintenance/work-orders${qs ? '?' + qs : ''}`, { headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to load work orders');
      setRows(Array.isArray(data) ? data : (data.rows || []));
    } catch {
      setError('Network error while loading work orders');
    } finally {
      setLoading(false);
    }
  };

  const loadRooms = async () => {
    if (!selectedProperty?.id) return;
    try {
      const res = await fetch('/api/v1/maintenance/rooms', { headers });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setRooms(Array.isArray(data) ? data : (data.rows || []));
    } catch { /* silent */ }
  };

  const loadTechnicians = async () => {
    if (!selectedProperty?.id) return;
    try {
      const res = await fetch('/api/v1/maintenance/technicians', { headers });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setTechnicians(Array.isArray(data) ? data : (data.rows || []));
    } catch { /* silent */ }
  };

  useEffect(() => { loadRows(); }, [selectedProperty, fStatus, fPriority, fCategory]);

  /* ---------- actions ---------- */

  const handleCreate = async () => {
    setError(''); setSuccess('');
    if (!form.description.trim()) return setError('Description is required');
    try {
      const body = { ...form };
      if (!body.room_id) { delete body.room_id; delete body.mark_room_ooo; }
      const res = await fetch('/api/v1/maintenance/work-orders', {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to create work order');
      setSuccess('Work order created: ' + (data.wo_number || data.id || ''));
      setShowCreate(false);
      setForm(emptyForm());
      loadRows();
    } catch {
      setError('Network error while creating work order');
    }
  };

  const handleStatusChange = (wo, newStatus) => {
    if (newStatus === 'CANCELLED' || newStatus === 'COMPLETED') {
      setPromptWO(wo);
      setPromptType(newStatus);
      setPromptText('');
      return;
    }
    patchStatus(wo.id, newStatus, {});
  };

  const confirmPrompt = () => {
    if (!promptText.trim()) { setError(promptType === 'CANCELLED' ? 'Cancellation reason is required' : 'Resolution notes are required'); return; }
    const extra = promptType === 'CANCELLED' ? { cancellation_reason: promptText } : { resolution_notes: promptText };
    patchStatus(promptWO.id, promptType, extra);
    setPromptWO(null);
    setPromptText('');
  };

  const patchStatus = async (id, status, extra) => {
    setError(''); setSuccess('');
    try {
      const res = await fetch(`/api/v1/maintenance/work-orders/${id}/status`, {
        method: 'PATCH', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, ...extra })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to update status');
      setSuccess('Status updated to ' + status);
      loadRows();
    } catch {
      setError('Network error');
    }
  };

  const handleAssign = async () => {
    setError(''); setSuccess('');
    if (!assignTechId) return setError('Select a technician');
    try {
      const res = await fetch(`/api/v1/maintenance/work-orders/${assignWO.id}/assign`, {
        method: 'PATCH', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ technician_id: assignTechId })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) return setError(data.error || 'Failed to assign');
      setSuccess('Assigned successfully');
      setAssignWO(null);
      setAssignTechId('');
      loadRows();
    } catch {
      setError('Network error');
    }
  };

  const openCreate = () => {
    setForm(emptyForm());
    setShowCreate(true);
    loadRooms();
  };

  const openAssign = (wo) => {
    setAssignWO(wo);
    setAssignTechId('');
    loadTechnicians();
  };

  const fmtDate = (d) => { if (!d) return '-'; try { return new Date(d).toLocaleString(); } catch { return d; } };

  /* ---------- styles ---------- */
  const s = {
    page: { padding: 24, fontFamily: "'Segoe UI', sans-serif", maxWidth: 1200, margin: '0 auto' },
    h2: { margin: '0 0 16px', fontSize: 22, fontWeight: 600, color: '#1a1a2e' },
    bar: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 },
    select: { padding: '6px 10px', borderRadius: 4, border: '1px solid #ccc', fontSize: 13 },
    btn: { padding: '7px 16px', borderRadius: 4, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600 },
    btnPrimary: { backgroundColor: '#0d6efd', color: '#fff' },
    btnSuccess: { backgroundColor: '#28a745', color: '#fff' },
    btnSecondary: { backgroundColor: '#6c757d', color: '#fff' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #dee2e6', backgroundColor: '#f8f9fa', fontWeight: 600, color: '#495057' },
    td: { padding: '8px 10px', borderBottom: '1px solid #eee', verticalAlign: 'middle' },
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 },
    modal: { backgroundColor: '#fff', borderRadius: 8, padding: isMobile ? 16 : 24, width: isMobile ? '100%' : 520, maxWidth: '95vw', ...(isMobile ? { boxSizing: 'border-box' } : {}), maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.25)' },
    modalSm: { backgroundColor: '#fff', borderRadius: 8, padding: isMobile ? 16 : 20, width: isMobile ? '100%' : 380, maxWidth: '95vw', ...(isMobile ? { boxSizing: 'border-box' } : {}), boxShadow: '0 8px 32px rgba(0,0,0,0.25)' },
    label: { display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4, color: '#333' },
    input: { width: '100%', padding: '6px 10px', borderRadius: 4, border: '1px solid #ccc', fontSize: 13, boxSizing: 'border-box' },
    row: { marginBottom: 12 },
    alertErr: { padding: '8px 12px', borderRadius: 4, backgroundColor: '#f8d7da', color: '#842029', fontSize: 13, marginBottom: 12 },
    alertOk: { padding: '8px 12px', borderRadius: 4, backgroundColor: '#d1e7dd', color: '#0f5132', fontSize: 13, marginBottom: 12 },
    checkbox: { marginRight: 6 }
  };

  /* ---------- render ---------- */

  if (!selectedProperty?.id) {
    return <div style={s.page}><p style={{ color: '#888' }}>Select a property to manage work orders.</p></div>;
  }

  return (
    <div style={s.page}>
      <h2 style={s.h2}>Maintenance Work Orders</h2>

      {error && <div style={s.alertErr}>{error} <span style={{ float: 'right', cursor: 'pointer' }} onClick={() => setError('')}>x</span></div>}
      {success && <div style={s.alertOk}>{success} <span style={{ float: 'right', cursor: 'pointer' }} onClick={() => setSuccess('')}>x</span></div>}

      {/* filter bar */}
      <div style={s.bar}>
        <select style={s.select} value={fStatus} onChange={e => setFStatus(e.target.value)}>
          <option value="">All Statuses</option>
          {STATUSES.map(st => <option key={st} value={st}>{st.replace(/_/g, ' ')}</option>)}
        </select>
        <select style={s.select} value={fPriority} onChange={e => setFPriority(e.target.value)}>
          <option value="">All Priorities</option>
          {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select style={s.select} value={fCategory} onChange={e => setFCategory(e.target.value)}>
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <button style={{ ...s.btn, ...s.btnPrimary }} onClick={openCreate}>+ New Work Order</button>
      </div>

      {/* table */}
      {loading ? <p style={{ color: '#888' }}>Loading...</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>WO#</th>
                <th style={s.th}>Category</th>
                <th style={s.th}>Room</th>
                <th style={s.th}>Priority</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Assigned To</th>
                <th style={s.th}>Created</th>
                <th style={s.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={8} style={{ ...s.td, textAlign: 'center', color: '#888', padding: 24 }}>No work orders found.</td></tr>
              )}
              {rows.map(wo => (
                <tr key={wo.id} style={{ transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f5f7ff'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = ''}>
                  <td style={s.td}>{wo.wo_number || wo.id}</td>
                  <td style={s.td}>{(wo.category || '').replace(/_/g, ' ')}</td>
                  <td style={s.td}>{wo.room_number || wo.room_id || '-'}</td>
                  <td style={s.td}><span style={badge(wo.priority, PRIORITY_COLORS[wo.priority] || '#888')}>{wo.priority}</span></td>
                  <td style={s.td}><span style={badge(wo.status, STATUS_COLORS[wo.status] || '#888')}>{(wo.status || '').replace(/_/g, ' ')}</span></td>
                  <td style={s.td}>{wo.assigned_to_name || wo.assigned_to || '-'}</td>
                  <td style={s.td}>{fmtDate(wo.created_at)}</td>
                  <td style={s.td}>
                    {hasMaint && (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <select style={{ ...s.select, fontSize: 12, padding: '3px 6px' }}
                                value="" onChange={e => { if (e.target.value) handleStatusChange(wo, e.target.value); }}>
                          <option value="">Status...</option>
                          {STATUSES.filter(st => st !== wo.status).map(st => (
                            <option key={st} value={st}>{st.replace(/_/g, ' ')}</option>
                          ))}
                        </select>
                        <button style={{ ...s.btn, ...s.btnSecondary, fontSize: 12, padding: '3px 10px' }}
                                onClick={() => openAssign(wo)}>Assign</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ---------- Create Modal ---------- */}
      {showCreate && (
        <div style={s.overlay} onClick={() => setShowCreate(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 18, color: '#1a1a2e' }}>New Work Order</h3>

            <div style={s.row}>
              <label style={s.label}>Category *</label>
              <select style={s.input} value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select>
            </div>

            <div style={s.row}>
              <label style={s.label}>Sub-category</label>
              <input style={s.input} value={form.sub_category} onChange={e => setForm(f => ({ ...f, sub_category: e.target.value }))} placeholder="e.g. Tap leak" />
            </div>

            <div style={s.row}>
              <label style={s.label}>Room</label>
              <select style={s.input} value={form.room_id} onChange={e => setForm(f => ({ ...f, room_id: e.target.value }))}>
                <option value="">-- None --</option>
                {rooms.map(r => <option key={r.room_id || r.id} value={r.room_id || r.id}>{r.room_number || r.room_no || r.id}</option>)}
              </select>
            </div>

            {form.room_id && (
              <div style={s.row}>
                <label style={{ ...s.label, fontWeight: 'normal', display: 'flex', alignItems: 'center' }}>
                  <input type="checkbox" style={s.checkbox} checked={form.mark_room_ooo} onChange={e => setForm(f => ({ ...f, mark_room_ooo: e.target.checked }))} />
                  Mark Room Out of Order
                </label>
              </div>
            )}

            <div style={s.row}>
              <label style={s.label}>Asset (optional)</label>
              <input style={s.input} value={form.asset} onChange={e => setForm(f => ({ ...f, asset: e.target.value }))} placeholder="e.g. AC Unit #3" />
            </div>

            <div style={s.row}>
              <label style={s.label}>Location (for non-room)</label>
              <input style={s.input} value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. Lobby corridor" />
            </div>

            <div style={s.row}>
              <label style={s.label}>Priority *</label>
              <select style={s.input} value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>

            <div style={s.row}>
              <label style={s.label}>Reported Source *</label>
              <select style={s.input} value={form.reported_source} onChange={e => setForm(f => ({ ...f, reported_source: e.target.value }))}>
                {SOURCES.map(src => <option key={src} value={src}>{src.replace(/_/g, ' ')}</option>)}
              </select>
            </div>

            <div style={s.row}>
              <label style={s.label}>Description *</label>
              <textarea style={{ ...s.input, minHeight: 70, resize: 'vertical' }} value={form.description}
                        onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Describe the issue..." />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button style={{ ...s.btn, ...s.btnSecondary }} onClick={() => setShowCreate(false)}>Cancel</button>
              <button style={{ ...s.btn, ...s.btnPrimary }} onClick={handleCreate}>Create</button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Assign Modal ---------- */}
      {assignWO && (
        <div style={s.overlay} onClick={() => setAssignWO(null)}>
          <div style={s.modalSm} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 16, color: '#1a1a2e' }}>
              Assign WO#{assignWO.wo_number || assignWO.id}
            </h3>
            <div style={s.row}>
              <label style={s.label}>Technician</label>
              <select style={s.input} value={assignTechId} onChange={e => setAssignTechId(e.target.value)}>
                <option value="">-- Select --</option>
                {technicians.map(t => (
                  <option key={t.id || t.user_id} value={t.id || t.user_id}>
                    {t.name || t.full_name || t.username || t.id}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button style={{ ...s.btn, ...s.btnSecondary }} onClick={() => setAssignWO(null)}>Cancel</button>
              <button style={{ ...s.btn, ...s.btnSuccess }} onClick={handleAssign}>Assign</button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- Prompt Modal (Cancel / Complete) ---------- */}
      {promptWO && (
        <div style={s.overlay} onClick={() => setPromptWO(null)}>
          <div style={s.modalSm} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 16, color: '#1a1a2e' }}>
              {promptType === 'CANCELLED' ? 'Cancellation Reason' : 'Resolution Notes'}
            </h3>
            <div style={s.row}>
              <textarea style={{ ...s.input, minHeight: 60, resize: 'vertical' }} value={promptText}
                        onChange={e => setPromptText(e.target.value)}
                        placeholder={promptType === 'CANCELLED' ? 'Why is this being cancelled?' : 'How was this resolved?'} />
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
              <button style={{ ...s.btn, ...s.btnSecondary }} onClick={() => setPromptWO(null)}>Back</button>
              <button style={{ ...s.btn, ...(promptType === 'CANCELLED' ? { backgroundColor: '#dc3545', color: '#fff' } : s.btnSuccess) }}
                      onClick={confirmPrompt}>
                {promptType === 'CANCELLED' ? 'Confirm Cancel' : 'Confirm Complete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MaintWorkOrders;
